import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { REFERRAL_STORAGE_KEY } from './ReferralLanding';
import {
  auth,
  googleLoginProvider,
  googleSignupProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  ensureUserProfileDocument,
} from '@/firebase';
import { getAdditionalUserInfo } from 'firebase/auth';
import { Mail, Lock, Home, AlertCircle, CheckCircle2, ShieldCheck, Globe, Database, User, Users, Gift } from 'lucide-react';

import { Button } from '@/components/ui/Button';

/**
 * 🔐 Login Component v3.0
 * Refactored for Hybrid Scoping. Ensures every login initializes the 
 * Personal Partition (/users/{uid}) before checking Household Hub status.
 */
export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const intent = new URLSearchParams(location.search).get('intent');

  const [authMode, setAuthMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);

  // If the user typed a code manually, write it to localStorage so trackReferralSignup picks it up
  function primeReferralCode() {
    const clean = referralCode.trim().toUpperCase();
    if (clean) localStorage.setItem(REFERRAL_STORAGE_KEY, clean);
  }

  // Fire referral signup event if user arrived via a referral link or entered a code manually
  async function trackReferralSignup() {
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!code) return;
    try {
      const fn = httpsCallable(getFunctions(getApp()), 'trackReferral');
      await fn({ code, event: 'signup' });
    } catch {
      // Non-fatal — referral tracking must never block account creation
    } finally {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    }
  }


  /**
   * 🛡️ Authentication Protocol
   * Adheres to Google API Limited Use Policy.
   */
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (authMode === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await ensureUserProfileDocument(userCredential.user);
        // Google sign-up already verifies the email intrinsically — only the email/password
        // path needs this. Non-fatal: a failed send shouldn't block account creation.
        sendEmailVerification(userCredential.user).catch((err) =>
          console.error('Failed to send verification email:', err)
        );
        primeReferralCode();
        await trackReferralSignup();
        navigate(intent === 'upgrade' ? '/upgrade' : '/dashboard');
      } else if (authMode === 'signin') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await ensureUserProfileDocument(userCredential.user);
        navigate(intent === 'upgrade' ? '/upgrade' : '/dashboard');
      } else if (authMode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setMessage(t('login.reset_email_sent', 'Security reset protocol initiated. Check your inbox.'));
        setAuthMode('signin');
      }
    } catch (err: any) {
      console.error('[EmailAuth]', err?.code, err?.message);
      let errorMessage = t('login.err_generic', 'Something went wrong. Please try again.');
      if (err.code === 'auth/email-already-in-use') errorMessage = t('login.err_email_in_use', 'This email is already registered.');
      else if (err.code === 'auth/weak-password') errorMessage = t('login.err_weak_password', 'Password must be at least 6 characters.');
      else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-login-credentials') errorMessage = t('login.err_invalid_credentials', 'Incorrect email or password.');
      else if (err.code === 'auth/too-many-requests') errorMessage = t('login.err_too_many_requests', 'Too many attempts. Please wait a moment and try again.');
      else if (err.code === 'auth/network-request-failed') errorMessage = t('login.err_network', 'Network error. Check your connection and try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError(null);

    const provider = authMode === 'signup' ? googleSignupProvider : googleLoginProvider;

    try {
      const userCredential = await signInWithPopup(auth, provider);
      await ensureUserProfileDocument(userCredential.user);
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser ?? false;
      // New SSO users who haven't pre-filled a code get a prompt before proceeding
      if (isNewUser && !referralCode.trim()) {
        setShowReferralPrompt(true);
        return;
      }
      primeReferralCode();
      await trackReferralSignup();
      navigate(intent === 'upgrade' ? '/upgrade' : '/dashboard');
    } catch (err: any) {
      console.error('[GoogleAuth]', err?.code, err?.message);
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        if (err.code === 'auth/network-request-failed') {
          setError(t('login.err_network', 'Network error. Check your connection and try again.'));
        } else if (err.code === 'auth/account-exists-with-different-credential') {
          setError(t('login.err_account_exists', 'An account already exists with this email. Try signing in with email/password.'));
        } else {
          setError(t('login.err_generic', 'Something went wrong. Please try again.'));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950 p-6 relative overflow-x-hidden">
      
      {/* 🎨 Decoration (Unchanged) */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* 🏛️ Brand Logo (Unchanged) */}
      <div onClick={() => navigate('/')} className="flex flex-col items-center gap-3 mb-10 cursor-pointer group z-10">
        <div className="p-3 rounded-2xl bg-brand text-white shadow-xl group-hover:scale-110 transition-transform">
          <Home className="w-8 h-8" />
        </div>
        <div className="flex items-center gap-1 font-black text-3xl tracking-tighter text-slate-950 dark:text-slate-200">
          {t('brand.name_part1', 'Everyday')}<span className="text-brand dark:text-blue-400">{t('brand.name_part2', 'OS')}</span>
        </div>
      </div>

      <div className="relative w-full max-w-md z-10">
        <div className="w-full p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl">

          {/* ── POST-SSO REFERRAL PROMPT (new Google users only) ── */}
          {showReferralPrompt && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-500/10 mb-4">
                  <Gift className="w-7 h-7 text-primary-400" />
                </div>
                <h2 className="text-2xl font-black text-slate-950 dark:text-slate-200 tracking-tight">Got a referral code?</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">Enter it below, or skip to go straight to your dashboard.</p>
              </div>
              <div className="relative">
                <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  maxLength={10}
                  autoFocus
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand rounded-2xl text-slate-950 dark:text-slate-200 font-bold outline-none transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
                  placeholder="Enter code"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setShowReferralPrompt(false);
                    navigate(intent === 'upgrade' ? '/upgrade' : '/dashboard');
                  }}
                  className="flex-1 rounded-2xl h-12 border-2 border-slate-100 dark:border-slate-800 font-bold text-sm"
                >
                  Skip
                </Button>
                <Button
                  size="lg"
                  onClick={async () => {
                    primeReferralCode();
                    await trackReferralSignup();
                    setShowReferralPrompt(false);
                    navigate(intent === 'upgrade' ? '/upgrade' : '/dashboard');
                  }}
                  disabled={!referralCode.trim()}
                  className="flex-1 rounded-2xl h-12 font-black text-xs uppercase bg-brand hover:bg-brand-hover text-white tracking-widest"
                >
                  Apply Code
                </Button>
              </div>
            </div>
          )}

          {/* ── MAIN FORM (hidden while referral prompt is showing) ── */}
          {!showReferralPrompt && <>

          {/* 🛡️ HYBRID SCOPING ANNOUNCEMENT */}
          <div className="mb-8 flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30">
             <div className="flex -space-x-2">
               <div className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                 <User className="w-3 h-3 text-emerald-500" />
               </div>
               <div className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                 <Users className="w-3 h-3 text-blue-500" />
               </div>
             </div>
             <p className="text-2xs font-black uppercase text-brand dark:text-blue-400 tracking-tighter">
                {t('login.hybrid_enabled', 'Personal + Shared spaces enabled')}
             </p>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-slate-950 dark:text-slate-200 mb-2 tracking-tight uppercase italic">
              {authMode === 'signin' ? t('login.enter_os', 'Enter OS') : t('login.initialize', 'Initialize')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-xs font-medium px-4">
              {authMode === 'signin' && t('login.signin_subtitle', 'Authorized Access Only.')}
              {authMode === 'signup' && t('login.signup_subtitle', 'Create your free account.')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300 font-bold">{error}</p>
            </div>
          )}

          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand rounded-2xl text-slate-950 dark:text-slate-200 font-bold outline-none transition-all"
                placeholder={t('login.email_placeholder', 'example@email.com')}

              />
            </div>

            {authMode !== 'reset' && (
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand rounded-2xl text-slate-950 dark:text-slate-200 font-bold outline-none transition-all"
                  placeholder={t('login.password_placeholder', 'password')}
                />
              </div>
            )}

            {authMode === 'signup' && (
              <div className="relative">
                <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  maxLength={10}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand rounded-2xl text-slate-950 dark:text-slate-200 font-bold outline-none transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
                  placeholder={t('login.referral_code_placeholder', 'Referral code (optional)')}
                />
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-2xl h-14 font-black text-xs uppercase bg-brand hover:bg-brand-hover text-white shadow-xl shadow-brand/20 tracking-widest"
              isLoading={loading}
            >
              {authMode === 'reset' ? t('login.dispatch_reset', 'DISPATCH RESET') : authMode === 'signup' ? t('login.initialize_personal_space', 'INITIALIZE PERSONAL SPACE') : t('login.login_btn', 'LOGIN')}
            </Button>
          </form>

          <div className="relative flex items-center justify-center my-8">
            <div className="border-t border-slate-100 dark:border-slate-800 w-full"></div>
            <span className="bg-white dark:bg-slate-900 px-4 text-xs font-medium text-slate-400 absolute">{t('login.oauth_bridge', 'or continue with')}</span>
          </div>

          <Button
            variant="outline"
            size="lg"
            onClick={handleGoogleAuth}
            disabled={loading}
            className="w-full rounded-2xl h-14 border-2 border-slate-100 dark:border-slate-800 font-bold"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 mr-3" alt="Google" />
            {authMode === 'signup'
              ? t('login.google_signup_btn', 'Sign up with Google')
              : t('login.google_signin_btn', 'Sign in with Google')}
          </Button>

          {/* Sign up / Sign in toggle */}
          {authMode !== 'reset' && (
            <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
              {authMode === 'signin' ? (
                <>{t('login.no_account', "Don't have an account?")}{' '}
                  <button onClick={() => { setAuthMode('signup'); setError(null); }} className="text-brand dark:text-blue-400 font-bold hover:underline">{t('login.sign_up', 'Sign up')}</button>
                </>
              ) : (
                <>{t('login.have_account', 'Already have an account?')}{' '}
                  <button onClick={() => { setAuthMode('signin'); setError(null); }} className="text-brand dark:text-blue-400 font-bold hover:underline">{t('login.sign_in', 'Sign in')}</button>
                </>
              )}
            </p>
          )}

          {/* ⚖️ GOOGLE COMPLIANCE DISCLOSURE */}
          <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
            <p className="text-2xs leading-relaxed text-slate-500 dark:text-slate-400 font-medium">
              {t('login.google_disclosure_part1', 'By continuing, EverydayOS will access your Google profile (name, email, and avatar) to initialize your personal space. We will also request access to your Google Calendar, allowing EverydayOS to')} <strong>{t('login.google_disclosure_bold', 'see, edit, share, and permanently delete events and calendars')}</strong> {t('login.google_disclosure_part2', 'that you can access. We use this data strictly for account identification and calendar functionality. We do not sell your data or use it for advertising. You can review Google\'s')}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" className="text-brand dark:text-blue-400 underline mx-1">{t('login.google_disclosure_link', 'Limited Use Policy')}</a>.
            </p>
          </div>

          </>}
        </div>

        {/* 🛡️ DATA SAFETY SIDECAR (Modified for Hybrid Info) */}
        <div className="lg:absolute lg:left-full lg:ml-8 lg:top-0 w-full lg:w-64 mt-8 lg:mt-0 space-y-4">
<div className="p-6 bg-brand rounded-[2rem] text-white shadow-xl flex flex-col gap-4">
<ShieldCheck className="w-6 h-6 opacity-80" />
<div>
<h4 className="font-black text-xs uppercase tracking-widest mb-1">{t('login.hybrid_partitioning_title', 'Hybrid Partitioning')}</h4>
<p className="text-2xs font-bold opacity-90 leading-relaxed">
{t('login.personal_data_target', 'Personal data is stored in your private space.')}
{t('login.shared_data_target', 'Shared data is visible to your household members.')}
</p>
</div>
<div className="pt-2 border-t border-white/20">
<h4 className="font-black text-[9px] uppercase tracking-[0.2em] mb-2 text-emerald-300">{t('login.privacy_protocol_title', 'Privacy Protocol 2026')}</h4>
<p className="text-[9px] font-bold opacity-80 leading-tight">
{t('login.zero_sharing_policy', 'Zero-sharing policy: Personal data is never exposed to third-party AI training or external surveillance.')} 
</p>
</div>
<ul className="space-y-2 pt-2 border-t border-white/20">
<li className="flex gap-2 items-center">
<div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
<span className="text-[9px] font-black uppercase tracking-wider">{t('login.verified_data_isolation', 'Verified Data Isolation')}</span>
</li>
<li className="flex gap-2 items-center">
<div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
<span className="text-[9px] font-black uppercase tracking-wider">{t('login.minimal_profile_access', 'Minimal Profile Access')}</span>
</li>
</ul>
<div className="bg-white/10 p-3 rounded-xl">
<p className="text-[8px] font-black uppercase tracking-tighter leading-none opacity-70">
{t('login.system_status', 'System Status:')}
</p>
{/*<p className="text-2xs font-bold text-emerald-300 mt-1 uppercase italic">
Privacy Compliant • Verified
</p>*/}
</div>
</div>
</div>
      </div>

      {/* --- Global Footer for Compliance and Language --- */}
      <div className="mt-10 flex items-center justify-center gap-4 text-xs font-medium text-slate-500 dark:text-slate-400 z-10">
        <a href="mailto:support@everydaytheapp.com" className="hover:underline">{t('login.footer_help', 'Help')}</a>
        <span className="text-slate-300 dark:text-slate-700">•</span>
        <Link to="/privacy" className="hover:underline">{t('login.footer_privacy', 'Privacy')}</Link>
        <span className="text-slate-300 dark:text-slate-700">•</span>
        <Link to="/terms" className="hover:underline">{t('login.footer_terms', 'Terms')}</Link>
        
        {/* Language Toggle */}
        <div className="relative group ml-4">
          <button className="flex items-center gap-1 hover:underline">
            <Globe className="w-3.5 h-3.5" />
            <span className="uppercase">{i18n.language}</span>
          </button>
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-800 p-2 rounded-lg shadow-lg hidden group-hover:block transition-all duration-200 ease-out z-20">
            <button 
              onClick={() => i18n.changeLanguage('en')} 
              className="block w-full text-left px-3 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md text-sm"
            >
              English
            </button>
            <button
              onClick={() => i18n.changeLanguage('fr')}
              className="block w-full text-left px-3 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md text-sm"
            >
              Français
            </button>
            <button
              onClick={() => i18n.changeLanguage('es')}
              className="block w-full text-left px-3 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md text-sm"
            >
              Español
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};